import { useEffect, useState } from 'preact/hooks';
import { AttentionSheet } from '../components/AttentionSheet';
import { Board } from '../components/Board';
import { DayChips } from '../components/DayChips';
import { Gate } from '../components/Gate';
import { Header } from '../components/Header';
import { NameBar } from '../components/NameBar';
import { NameGate } from '../components/NameGate';
import { CompleteSheet, MissSheet } from '../components/Sheets';
import { Tabs } from '../components/Tabs';
import { Toast } from '../components/Toast';
import { AdminHubSheet } from '../components/admin/AdminHubSheet';
import { EditCallSheet } from '../components/admin/EditCallSheet';
import { HistorySheet } from '../components/admin/HistorySheet';
import { HubSidebar } from '../components/admin/HubSidebar';
import { JobDetailSheet } from '../components/admin/JobDetailSheet';
import { JobsDbSheet } from '../components/admin/JobsDbSheet';
import { NewWeekSheet } from '../components/admin/NewWeekSheet';
import { RemindersSheet } from '../components/admin/RemindersSheet';
import { ReturnSheet } from '../components/admin/ReturnSheet';
import { authRequired } from '../lib/api';
import { localMondayISO } from '../lib/dates';
import { refreshCanEnable, syncPushSub } from '../lib/push';
import { drain } from '../lib/queue';
import { adminActive, restoreAdmin } from '../state/admin';
import {
  editSheet,
  jobDetail,
  returnFor,
  showHistory,
  showHubSheet,
  showJobsDb,
  showNewWeek,
  showReminders,
} from '../state/adminUi';
import { refreshCompletions } from '../state/completions';
import { checkIn } from '../state/presence';
import { fetchReminders } from '../state/reminders';
import type { CallView } from '../state/schedule';
import { loadWeek, loadWeeks, week } from '../state/schedule';
import { fetchRole, needsName, techName } from '../state/session';
import { toast } from '../state/toast';

const POLL_MS = 60_000;

async function boot(): Promise<void> {
  await Promise.all([loadWeeks(), loadWeek(), refreshCompletions(), fetchRole()]);
  await restoreAdmin();
  void checkIn();
  if (adminActive.value) void fetchReminders().catch(() => {});
  // Push upkeep (v1): refresh the server's copy of this device's subscription and
  // quietly re-key it if VAPID ever rotated; then decide whether to offer the button.
  void syncPushSub(techName.value);
  void refreshCanEnable();
}

export function App() {
  const [sheet, setSheet] = useState<{ kind: 'done' | 'missed'; call: CallView } | null>(null);
  const [attention, setAttention] = useState(false);

  useEffect(() => {
    void boot().catch(() => {});
    // Poll while visible + online; any overlay open pauses it (v1 calm-poll rules).
    const tick = () => {
      if (document.hidden || !navigator.onLine || authRequired.value) return;
      void loadWeek().catch(() => {});
      void refreshCompletions().catch(() => {});
      void checkIn();
      if (adminActive.value) void fetchReminders().catch(() => {});
    };
    const t = setInterval(tick, POLL_MS);
    const onVis = () => !document.hidden && tick();
    const onOnline = () => {
      toast('Back online — syncing.');
      void drain();
      tick();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('online', onOnline);
    // A fresh name (name gate) shows up on the People roster right away.
    const stopName = techName.subscribe((n) => {
      if (n) void checkIn();
    });
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('online', onOnline);
      stopName();
    };
  }, []);

  // Auth gate re-runs the boot sequence after unlock (v1 contract).
  if (authRequired.value) {
    return (
      <GateWrapper
        onUnlocked={() => {
          void boot().catch(() => {});
        }}
      />
    );
  }
  if (needsName.value) return <NameGate />;

  const admin = adminActive.value;
  const viewingArchived = !!week.value?.weekId && week.value.weekId < localMondayISO();

  return (
    <div class={`min-h-dvh bg-canvas font-sans ${admin ? 'lg:pl-72' : ''}`}>
      {admin && <HubSidebar />}
      <Header
        onRefresh={() => {
          void boot()
            .then(() => toast('Up to date.'))
            .catch(() => toast('Could not refresh.'));
        }}
        onAttention={() => setAttention(true)}
      />
      {viewingArchived && (
        <p
          class="bg-note-amber px-3 py-1.5 text-center text-xs font-bold text-note-ink"
          data-testid="archive-banner"
        >
          Viewing an archived week — {week.value?.weekOf}
        </p>
      )}
      <DayChips />
      <NameBar />
      <main>
        <Board
          onDone={(call) => setSheet({ kind: 'done', call })}
          onMissed={(call) => setSheet({ kind: 'missed', call })}
          onRetry={() => void boot().catch(() => {})}
        />
      </main>
      <Tabs />
      {sheet?.kind === 'done' && <CompleteSheet call={sheet.call} onClose={() => setSheet(null)} />}
      {sheet?.kind === 'missed' && <MissSheet call={sheet.call} onClose={() => setSheet(null)} />}
      {attention && <AttentionSheet onClose={() => setAttention(false)} />}
      {admin && <AdminOverlays />}
      <Toast />
      <div id="rotateLock" role="status">
        <svg
          viewBox="0 0 24 24"
          class="h-10 w-10"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          aria-hidden="true"
        >
          <rect x="7" y="3" width="10" height="18" rx="2" />
          <path d="M2 12a10 10 0 0 1 3-7M22 12a10 10 0 0 1-3 7" stroke-linecap="round" />
        </svg>
        <p class="font-semibold">Please rotate your phone</p>
      </div>
    </div>
  );
}

/**
 * Admin overlay sheets in ONE dedicated parent, every child EXPLICITLY keyed. DOM
 * order must equal this JSX order (it decides both paint stacking and which sheet
 * Escape closes) — mixing keyed and unkeyed conditional siblings lets Preact insert a
 * new keyed sheet BEFORE its open siblings (WebKit-visible mis-stacking), and keying
 * everything in an isolated parent is what makes the order deterministic.
 */
function AdminOverlays() {
  return (
    <div>
      {showHubSheet.value && <AdminHubSheet key="hub" />}
      {showJobsDb.value && <JobsDbSheet key="jobs" />}
      {showHistory.value && <HistorySheet key="history" />}
      {showReminders.value && <RemindersSheet key="reminders" />}
      {jobDetail.value && (
        <JobDetailSheet key={`detail-${jobDetail.value.id}`} job={jobDetail.value} />
      )}
      {returnFor.value && (
        <ReturnSheet key={`return-${returnFor.value.id}`} target={returnFor.value} />
      )}
      {showNewWeek.value && <NewWeekSheet key="new-week" />}
      {editSheet.value && (
        <EditCallSheet key={`edit-${editSheet.value.id ?? 'add'}`} state={editSheet.value} />
      )}
    </div>
  );
}

/** Gate that notifies the shell when the session becomes valid again. */
function GateWrapper({ onUnlocked }: { onUnlocked: () => void }) {
  useEffect(() => {
    const stop = authRequired.subscribe((locked) => {
      if (!locked) onUnlocked();
    });
    return stop;
    // mount-only subscription on purpose: the gate lives exactly as long as the lock
  }, []);
  return <Gate />;
}
